import React, { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Label } from '../ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { useStudents } from '../../contexts/StudentsContext';
import { GENDER_OPTIONS, type ExportOptions } from '../../types/student';
import { describeExportScope, exportStudentsToExcel, selectStudentsForExport } from '../../utils/excel';
import { uniqueClasses } from '../../utils/stats';
interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function ExportDialog({
  open,
  onOpenChange
}: ExportDialogProps) {
  const {
    students
  } = useStudents();
  const classes = useMemo(() => uniqueClasses(students), [students]);
  const [options, setOptions] = useState<ExportOptions>({
    scope: 'all',
    className: '',
    gender: ''
  });
  const [exporting, setExporting] = useState(false);
  const selection = useMemo(() => selectStudentsForExport(students, options), [students, options]);
  const blocked = options.scope === 'class' && !options.className || options.scope === 'gender' && !options.gender;
  const handleExport = async () => {
    if (selection.length === 0) {
      toast.warning('No records match this filter.');
      return;
    }
    setExporting(true);
    await new Promise(resolve => setTimeout(resolve, 240));
    try {
      const fileName = exportStudentsToExcel(selection, options);
      toast.success(`Exported ${selection.length} record${selection.length === 1 ? '' : 's'}`, {
        description: fileName
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Excel export failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setExporting(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-85" data-mp="1ah6s033aww2r">
      <DialogContent className="glass-strong max-w-lg rounded-3xl" data-id="element-86" data-mp="1fhbbm1zia6x7">
        <DialogHeader data-id="element-87" data-mp="1fimf00p1hcns">
          <DialogTitle className="flex items-center gap-2 text-lg" data-id="element-88" data-mp="1feynf735n0bp">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500/15 text-emerald-600" data-id="element-89" data-mp="11j4n3cgg5ja7">
              <FileSpreadsheet className="h-4 w-4" data-id="element-90" data-mp="16gwm0qfie5nw" />
            </span>
            Configure Excel export
          </DialogTitle>
          <DialogDescription data-id="element-91" data-mp="1y7sz9s5hecbp">
            Records are compiled live from the Firestore <span className="font-mono" data-id="element-92" data-mp="1y9x206rn95ae">students</span> collection.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2" data-id="element-93" data-mp="1pfip2aul66ug">
          <div className="space-y-2" data-id="element-94" data-mp="1peh5tj3f2e1n">
            <Label data-id="element-95" data-mp="1pdeg3ehtyhnb">Export scope</Label>
            <Select value={options.scope} onValueChange={scope => setOptions({
            scope: scope as ExportOptions['scope'],
            className: '',
            gender: ''
          })} data-id="element-96" data-mp="2d4jv5rfy55wk">
              <SelectTrigger className="w-full rounded-xl" data-id="element-97" data-mp="2r9amgczzm5mm">
                <SelectValue placeholder="Choose a scope" data-id="element-98" data-mp="2n99vxtzm63z7" />
              </SelectTrigger>
              <SelectContent data-id="element-99" data-mp="310f099dxfkfv">
                <SelectItem value="all" data-id="element-100" data-mp="310okshg7cze6">All students</SelectItem>
                <SelectItem value="class" data-id="element-101" data-mp="2w1bufmq4ha6u">Filter by class</SelectItem>
                <SelectItem value="gender" data-id="element-102" data-mp="2w23qszrjnby6">Filter by gender</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {options.scope === 'class' && <div className="space-y-2" data-id="element-103" data-mp="24bh4bnufxgh4">
              <Label data-id="element-104" data-mp="24cifdsbgi2yu">Class</Label>
              {classes.length === 0 ? <p className="rounded-xl border border-dashed border-[var(--glass-hairline)] p-3 text-sm text-muted-foreground" data-id="element-105" data-mp="2ib8grk5b6ot0">
                  No classes recorded yet.
                </p> : <Select value={options.className} onValueChange={className => setOptions({
            ...options,
            className
          })} data-id="element-106" data-mp="3tr6rdsg4xiwc">
                  <SelectTrigger className="w-full rounded-xl" data-id="element-107" data-mp="1y95ggwoh6446">
                    <SelectValue placeholder="Select a class" data-id="element-108" data-mp="1yaqaml6vnl1i" />
                  </SelectTrigger>
                  <SelectContent data-id="element-109" data-mp="2388majca4ed7">
                    {classes.map(className => <SelectItem key={className} value={className} data-id="element-110" data-mp="285ia5uk0shyx">
                        {className}
                      </SelectItem>)}
                  </SelectContent>
                </Select>}
            </div>}

          {options.scope === 'gender' && <div className="space-y-2" data-id="element-111" data-mp="2m503mn89gjem">
              <Label data-id="element-112" data-mp="2qxt95dbjzvrr">Gender</Label>
              <Select value={options.gender} onValueChange={gender => setOptions({
            ...options,
            gender: gender as ExportOptions['gender']
          })} data-id="element-113" data-mp="2qyvxbdhhrk3z">
                <SelectTrigger className="w-full rounded-xl" data-id="element-114" data-mp="2zzalqlpr5fbp">
                  <SelectValue placeholder="Select a gender" data-id="element-115" data-mp="34xc4t6n6hen6" />
                </SelectTrigger>
                <SelectContent data-id="element-116" data-mp="t9z4cujtrj0r">
                  {GENDER_OPTIONS.map(gender => <SelectItem key={gender} value={gender} data-id="element-117" data-mp="t7mdfrw39ybk">
                      {gender}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>}

          <div className="flex items-center justify-between rounded-2xl border border-[var(--glass-hairline)] bg-white/50 px-4 py-3 dark:bg-white/5" data-id="element-118" data-mp="faheebuslzpt">
            <div data-id="element-119" data-mp="1bzoydffrk0rm">
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-id="element-120" data-mp="1c2b55cutyc8s">Ready to export</p>
              <p className="text-sm font-medium text-foreground" data-id="element-121" data-mp="181954tt39w5r">{describeExportScope(options)}</p>
            </div>
            <p className="font-mono text-2xl font-semibold text-foreground" data-id="element-122" data-mp="y9bqfd2vgm2a">{selection.length}</p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end" data-id="element-123" data-mp="1voppnaa44mm5">
          <Button variant="ghost" className="h-10 rounded-xl" onClick={() => onOpenChange(false)} data-id="element-124" data-mp="1qx7lkdcsm5wy">
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={exporting || blocked} className="h-10 rounded-xl bg-emerald-600 px-5 text-white hover:bg-emerald-700" data-id="element-125" data-mp="29uj7u3rh7os4">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-126" data-mp="211fo5dq8mfp1" /> : <Download className="mr-2 h-4 w-4" data-id="element-127" data-mp="252ri1vg59n31" />}
            {exporting ? 'Generating…' : 'Download .xlsx'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
}